import React from "react";
import { Toolbar } from "@mui/material";
import { Button } from "../../Button/Button";
import { HeartIcon } from "../../Icons/Miscellaneous/HeartIcon";
import { BellIcon } from "../../Icons/AlertsNotifications/BellIcon";
import { HamburgerMenuIcon } from "../../Icons/Media/HamburgerMenuIcon";
import { useDispatch, useSelector } from "react-redux";
import { setActiveDrawer } from "../../../Slices/uiSlice";
import {
  markNotificacionesAsRead,
  resetUnreadCount,
} from "../../../Slices/NotificacionesUsuario/notificacionesUsuarioSlice";
import { DRAWER_TYPES } from "Constantes/drawers";
import logoFull from "../../../Recursos/genesis_logo_full.webp";
import { useNavigate } from "react-router-dom";
import { RUTAS } from "rutas";
import { paramService } from "Services/param_service";
import { useQuery } from "Hooks/useQuery";
import { storageService } from "Services/storage_service";
import Badge from "@mui/material/Badge";
import "./NavbarDesktop.scss";

/**
 * NavbarDesktop component que renderiza la barra de navegación para dispositivos tablet y desktop.
 * Adapta su layout y funcionalidad según el estado de autenticación del usuario.
 *
 * @param {Object} props - Las props del componente
 * @param {boolean} props.isAuthenticated - Indica si el usuario está autenticado
 * @returns {JSX.Element} Toolbar desktop con logo, texto/botones de acceso y acciones de usuario
 */
export const NavbarDesktop = ({ isAuthenticated }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeDrawer = useSelector((state) => state.ui.activeDrawer);
  const { notificacionesUsuario, unreadCount } = useSelector(
    (state) => state.notificacionesUsuario
  );
  const cookieUser = storageService.getItem("user");

  const { data: images } = useQuery(paramService.param, {
    grupo: "imagenes",
    files: true,
  });

  const logoImg =
    images?.data.find((item) => item.codigoParametro === "logo_genesis")?.url ||
    "#";

  const handleLoginClick = () => {
    dispatch(setActiveDrawer(DRAWER_TYPES.LOGIN));
  };

  const handleMenuClick = () => {
    dispatch(setActiveDrawer(DRAWER_TYPES.MENU));
  };

  const handleFavoritesClick = () => {
    navigate(RUTAS.MIS_FAVORITOS);
  };

  const handleNotificationsClick = async () => {
    const isOpening = activeDrawer !== DRAWER_TYPES.NOTIFICATIONS;

    if (isOpening && cookieUser?.idUsuario) {
      dispatch(resetUnreadCount());
      
      if (notificacionesUsuario.length > 0) {
        const unreadNotifications = notificacionesUsuario
          .filter((notification) => !notification.fechaLectura)
          .map((notification) => ({
            idNotificacionUsuario: notification.idNotificacionUsuario,
            fechaLectura: new Date().toISOString(),
          }));

        if (unreadNotifications.length > 0) {
          await dispatch(markNotificacionesAsRead(unreadNotifications));
        }
      }
    }

    dispatch(setActiveDrawer(activeDrawer ? null : DRAWER_TYPES.NOTIFICATIONS));
  };

  const handleLogoClick = () => {
    navigate(RUTAS.PRINCIPAL);
    dispatch(setActiveDrawer(null));
  };


  if (!isAuthenticated) {
    return (
      <Toolbar className="navbar-desktop">
        <div className="navbar-desktop__logo" onClick={handleLogoClick}>
          <img
            src={logoImg}
            alt="Genesis Logo"
            onError={(e) => {
              e.target.src = logoFull;
            }}
          />
        </div>
        <div className="navbar-desktop__right">
          <span className="navbar-desktop__text">¿Ya tienes cuenta?</span>
          <Button
            variant="secundario-cta"
            size="mediano"
            className="h-11 paragraph-s-bold text-cian"
            onClick={handleLoginClick}
          >
            INICIAR SESIÓN
          </Button>
        </div>
      </Toolbar>
    );
  }

  return (
    <Toolbar className="navbar-desktop">
      <div className="navbar-desktop__logo" onClick={handleLogoClick}>
        <img
          src={logoImg}
          alt="Genesis Logo"
          onError={(e) => {
            e.target.src = logoFull;
          }}
        />
      </div>
      <div className="navbar-desktop__actions">
        <Button variant="icon" onClick={handleFavoritesClick}>
          <HeartIcon />
        </Button>
        <Button variant="icon" onClick={handleNotificationsClick}>
          <Badge
            badgeContent={unreadCount}
            className="navbar-desktop__notification-badge"
          >
            <BellIcon />
          </Badge>
        </Button>
        <Button variant="icon" onClick={handleMenuClick}>
          <HamburgerMenuIcon className="navbar-desktop__menu-icon" />
        </Button>
      </div>
    </Toolbar>
  );
};
